import { toast } from "react-toastify";
import { useMemo, useState } from "react";
import I18n from "common/i18n";
import { MOCK_PUBLIC_PIECES } from "features/builder/mockData";
import { MapCard, MapDetailPage, PublicMapsHeader } from "features/builder/elements";
import {
  MockPiece,
  PieceListContext,
  PieceCardAction,
  PublicFilter,
  PieceSortOrder,
} from "features/builder/pieceList";

export const PublicMapsTab = () => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [keyword, setKeyword] = useState("");
  const [activeFilters, setActiveFilters] = useState<PublicFilter[]>([]);
  const [sortOrder, setSortOrder] = useState<PieceSortOrder>(PieceSortOrder.DESC);

  const visibleMaps = useMemo(() => {
    let list = [...MOCK_PUBLIC_PIECES];
    if (activeFilters.includes(PublicFilter.OFFICIAL)) {
      list = list.filter((m) => m.is_template);
    }
    if (activeFilters.includes(PublicFilter.COPIED)) {
      list = list.filter((m) => m.copied_piece_id != null);
    }
    const kw = keyword.trim();
    if (kw) {
      list = list.filter((m) => m.name.includes(kw) || m.tags.includes(kw));
    }
    list.sort((a, b) =>
      sortOrder === PieceSortOrder.DESC ? b.liked - a.liked : a.liked - b.liked,
    );
    return list;
  }, [keyword, activeFilters, sortOrder]);

  const selected = MOCK_PUBLIC_PIECES.find((m) => m.piece_id === selectedId) ?? null;

  const handleToggleFilter = (kind: PublicFilter) => {
    setActiveFilters((prev) =>
      prev.includes(kind) ? prev.filter((k) => k !== kind) : [...prev, kind],
    );
  };

  const handleAction = (piece: MockPiece) => (action: PieceCardAction) => {
    switch (action) {
      case PieceCardAction.DETAIL:
        setSelectedId(piece.piece_id);
        break;
      case PieceCardAction.PLAY:
        toast(I18n.t("MSG_BUILDER_BTN_PLAY"));
        break;
      case PieceCardAction.COPY:
        toast(I18n.t("MSG_BUILDER_COPY_TOAST"));
        break;
      case PieceCardAction.LIKE:
        toast(I18n.t("MSG_BUILDER_LIKE_TOAST"));
        break;
      case PieceCardAction.REACTION:
        toast(I18n.t("MSG_BUILDER_MENU_REACTION"));
        break;
      case PieceCardAction.REPORT:
        toast(I18n.t("MSG_BUILDER_MENU_REPORT"));
        break;
      default:
        break;
    }
  };

  if (selected) {
    return (
      <MapDetailPage
        piece={selected}
        context={PieceListContext.PUBLIC}
        onBack={() => setSelectedId(null)}
        onAction={handleAction(selected)}
      />
    );
  }

  return (
    <div className="flex-col-el relative w-full h-full p-4 gap-3">
      <PublicMapsHeader
        count={visibleMaps.length}
        keyword={keyword}
        activeFilters={activeFilters}
        sortOrder={sortOrder}
        onChangeKeyword={setKeyword}
        onToggleFilter={handleToggleFilter}
        onToggleSort={() =>
          setSortOrder((o) =>
            o === PieceSortOrder.DESC ? PieceSortOrder.ASC : PieceSortOrder.DESC,
          )
        }
      />

      <div className="flex-col-el flex-1 w-full overflow-y-auto invisible-scrollbar">
        <div className="flex-row-el flex-wrap items-start justify-start gap-4 pb-4">
          {visibleMaps.map((m) => (
            <MapCard
              key={m.piece_id}
              piece={m}
              context={PieceListContext.PUBLIC}
              onAction={handleAction(m)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
